import { ZOOM_MAX_PARTICIPANTS } from '@/utils/constants'

import { GATING_GROUP_TO_LABEL, GATING_GROUPS } from '@/queries/constants'
import { SERIES_DESCRIPTION_MIN_CHARS } from '@/client/services/session/constants'
import {
  type SeriesTemplateDetails,
  SeriesTemplatePeerReviewVisibility,
} from '@/client/services/template'

import type { FormInputType } from '@/components/UI/Form/types'

import { AUDITOR_TAG_LABELS, AUDITOR_TAGS } from '../tags'
import {
  SERIES_TEMPLATE_SCHEDULE_TYPE_TO_LABEL,
  SERIES_TEMPLATE_SCHEDULE_TYPES,
  SERIES_TEMPLATE_TYPE_STANDARD,
  SERIES_TEMPLATE_TYPE_TO_LABEL,
  SERIES_TEMPLATE_TYPES,
} from '../template/constants'
import { getTutorQuestionnaireQuestion, type TutorQuestionnaire } from '../tutorQuestionnaire'

export default function getSeriesTemplateFormInputs({
  seriesTemplate,
  courseOptions,
  worldOptions,
  tutorTrainingOptions,
}: {
  seriesTemplate?: SeriesTemplateDetails | null
  courseOptions: { label: string; value: string }[]
  worldOptions: { label: string; value: string }[]
  tutorTrainingOptions: { label: string; value: string }[]
}): FormInputType[] {
  const formInputs: FormInputType[] = [
    {
      label: 'Title',
      type: 'text',
      name: 'title',
      initialValue: seriesTemplate?.title,
      required: true,
      size: 6,
    },
    {
      label: 'Slug',
      sublabel: `A shorthand code that the Schoolhouse engineering team can use to easily refer to this template. This should not be changed without notifying the engineering team. If you're confused, leave this field be!`,
      placeholder: 'caw-essay-2024-08',
      type: 'text',
      name: 'slug',
      initialValue: seriesTemplate?.slug,
      required: false,
      size: 6,
    },
    {
      label: 'Active?',
      sublabel: 'Is this template visible on the schedule series page?',
      type: 'radio',
      name: 'active',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      initialValue: seriesTemplate?.active == null ? null : seriesTemplate.active ? 'yes' : 'no',
      required: true,
      size: 6,
    },
    {
      label: 'Enabled?',
      sublabel: 'Can tutors create a series using this template right now?',
      type: 'radio',
      name: 'enabled',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      initialValue: seriesTemplate?.enabled == null ? null : seriesTemplate.enabled ? 'yes' : 'no',
      required: true,
      size: 6,
    },
    {
      label: 'External Description',
      sublabel: 'This is for tutors to see. Why should they host it?',
      type: 'rich-text',
      name: 'externalDescription',
      initialValue: seriesTemplate?.externalDescription,
      required: true,
      size: 6,
    },
    {
      label: `Description (${SERIES_DESCRIPTION_MIN_CHARS}+ characters)`,
      sublabel: 'This is for learners to see.',
      type: 'rich-text',
      name: 'description',
      initialValue: seriesTemplate?.description,
      required: true,
      size: 6,
      minLength: SERIES_DESCRIPTION_MIN_CHARS,
    },
    {
      label: 'Welcome Question',
      type: 'textarea',
      name: 'welcomeQuestion',
      initialValue: getTutorQuestionnaireQuestion(
        (seriesTemplate?.questionnaire as TutorQuestionnaire) ?? null
      ),
      required: false,
      size: 6,
    },
    {
      label: 'Attendance Policy',
      type: 'textarea',
      name: 'attendancePolicy',
      initialValue: seriesTemplate?.attendancePolicy,
      required: true,
      size: 6,
    },
    {
      label: 'Course',
      type: 'select',
      name: 'courseId',
      initialValue: seriesTemplate?.course?.id == null ? '' : `${seriesTemplate?.course.id}`,
      options: courseOptions,
      size: 6,
      required: false,
    },
    {
      label: 'Subworld',
      type: 'select',
      name: 'worldId',
      options: worldOptions,
      initialValue: seriesTemplate?.world?.id == null ? '' : `${seriesTemplate.world.id}`,
      size: 6,
      required: false,
    },
    {
      label: 'Gating Group',
      type: 'select',
      name: 'gatingGroup',
      options: GATING_GROUPS.map(gatingGroup => ({
        value: gatingGroup,
        label: GATING_GROUP_TO_LABEL[gatingGroup],
      })),
      initialValue: seriesTemplate?.gatingGroup,
      size: 6,
    },
    {
      label: 'Schedule Type',
      type: 'select',
      name: 'scheduleType',
      options: SERIES_TEMPLATE_SCHEDULE_TYPES.map(item => ({
        value: item,
        label: SERIES_TEMPLATE_SCHEDULE_TYPE_TO_LABEL[item],
      })),
      initialValue: seriesTemplate?.scheduleType,
      required: true,
      size: 6,
    },
    {
      label: 'Type',
      sublabel:
        'Rolling templates allow tutors to pick a start week instead of using a fixed minimum start date.',
      type: 'select',
      name: 'type',
      options: SERIES_TEMPLATE_TYPES.map(item => ({
        value: item,
        label: SERIES_TEMPLATE_TYPE_TO_LABEL[item],
      })),
      initialValue: seriesTemplate?.type ?? SERIES_TEMPLATE_TYPE_STANDARD,
      required: true,
      size: 6,
    },
    {
      label: 'Max Learners',
      type: 'number',
      name: 'maxLearners',
      sublabel: `You should not submit a number greater than ${ZOOM_MAX_PARTICIPANTS}`,
      required: true,
      initialValue: seriesTemplate?.maxLearners == null ? null : seriesTemplate?.maxLearners,
      size: 6,
      max: ZOOM_MAX_PARTICIPANTS,
      min: 1,
    },
    {
      label: 'Allow Co-hosting',
      type: 'radio',
      name: 'allowCohosts',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      initialValue:
        seriesTemplate?.allowCohosts == null ? null : seriesTemplate.allowCohosts ? 'yes' : 'no',
      size: 6,
    },
    {
      label: 'Assessment IDs',
      sublabel: 'Please input a comma-separated list of all assessment IDs, with no spaces.',
      type: 'text',
      name: 'assessmentIds',
      initialValue: seriesTemplate?.assessmentIds.join(','),
      required: false,
      size: 6,
    },
    {
      label: 'Curriculum URL',
      sublabel: 'The URL of the curriculum for this series.',
      type: 'text',
      name: 'curriculumUrl',
      initialValue: seriesTemplate?.curriculumUrl,
      required: false,
      size: 6,
    },
    {
      label: '[DEPRECATED] Curriculum',
      sublabel: 'Please fill out the curriculum URL instead of this field',
      type: 'rich-text',
      editorType: 'advanced',
      name: 'curriculum',
      initialValue: seriesTemplate?.curriculum,
      required: false,
      size: 6,
    },
    {
      label: 'Minimum start date',
      sublabel:
        'For recurring series, the first session will be the soonest chosen day on or after this date.',
      type: 'date',
      name: 'minimumStartDate',
      initialValue: seriesTemplate?.minimumStartDate?.toISO(),
      required: false,
      size: 6,
    },
    {
      label: 'Maximum registrations per learner',
      sublabel: `A learner can only be registered for this number of series created from this template at a given time. Series that have ended don't count towards this quota.`,
      type: 'number',
      name: 'maxRegistrationsPerLearner',
      initialValue: seriesTemplate?.maxRegistrationsPerLearner,
      required: false,
      size: 6,
    },
    {
      label: 'Maximum active series per tutor',
      sublabel: `A tutor can only be hosting this number of series created from this template at a given time. Series that have ended don't count towards this quota.`,
      type: 'number',
      name: 'maxActiveSeriesPerTutor',
      initialValue: seriesTemplate?.maxActiveSeriesPerTutor,
      required: false,
      size: 6,
    },
    {
      label: 'Peer Review Tags',
      sublabel:
        'Select all peer review tags that should be used for this series. These tags determine which types of sessions appear in the peer review queue.',
      type: 'multi-checkbox',
      options: AUDITOR_TAGS.map(tag => ({ label: AUDITOR_TAG_LABELS[tag] ?? tag, value: tag })),
      name: 'peerReviewTags',
      initialValue: seriesTemplate?.peerReviewTags ?? [],
      required: true,
      size: 6,
    },
    {
      label: 'Peer Review Visibility',
      sublabel: 'Unlisted sessions are only shown in the peer review queue using a special link.',
      type: 'radio',
      options: [
        { label: 'public', value: SeriesTemplatePeerReviewVisibility.Public },
        { label: 'unlisted', value: SeriesTemplatePeerReviewVisibility.Unlisted },
      ],
      name: 'peerReviewVisibility',
      initialValue:
        seriesTemplate?.peerReviewVisibility ?? SeriesTemplatePeerReviewVisibility.Public,
      required: true,
      size: 6,
    },
    {
      label: 'Required training',
      sublabel: 'Tutors must complete this training in order to host a series using this template',
      type: 'select',
      name: 'tutorTrainingId',
      options: tutorTrainingOptions,
      required: false,
      initialValue:
        seriesTemplate?.tutorTraining?.id == null ? null : `${seriesTemplate?.tutorTraining.id}`,
      size: 6,
    },
  ]

  return formInputs
}
